(() => {
  const rootSelector = "[data-ae-cart-drawer]";
  const initializedAttribute = "data-ae-cart-drawer-initialized";
  const documentInitializedAttribute = "data-ae-cart-drawer-document-initialized";
  const deferredOpenFormSelector = ".feature-chart__add-to-cart-form";
  const noUpsellsDrawerClass = "ae-cart-drawer--no-upsells";
  const noUpsellsInnerClass = "ae-cart-drawer__inner--no-upsells";
  const toastDelay = 1800;
  const accessoryDragThreshold = 5;
  const toastTimers = new WeakMap();
  const accessoryDotFrames = new WeakMap();
  const accessoryRootFrames = new WeakMap();
  const accessoryScrollAnimations = new WeakMap();
  const accessoryMutationObservers = new WeakMap();
  let activeAccessoryDrag = null;
  let deferredCartOpenPending = false;
  let deferredCartOpenTimer = null;

  function toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function toPixelNumber(value, fallback = 0) {
    const number = Number(String(value).replace("px", ""));
    return Number.isFinite(number) ? number : fallback;
  }

  function syncQuantityButtons(container) {
    const input = container?.querySelector("[data-line-key]");
    const decreaseButton = container?.querySelector('[data-ae-cart-quantity-adjust="decrease"]');
    const increaseButton = container?.querySelector('[data-ae-cart-quantity-adjust="increase"]');

    if (!input || !decreaseButton || !increaseButton) {
      return;
    }

    const min = toNumber(input.getAttribute("min"), 0);
    const maxAttribute = input.getAttribute("max");
    const max = maxAttribute === null || maxAttribute === "" ? Infinity : toNumber(maxAttribute, Infinity);
    const value = toNumber(input.value, min);

    decreaseButton.disabled = input.disabled || value <= min;
    increaseButton.disabled = input.disabled || value >= max;
  }

  function setLineBusy(lineItem, isBusy) {
    if (!lineItem) {
      return;
    }

    lineItem.toggleAttribute("aria-busy", isBusy);

    lineItem.querySelectorAll("[data-ae-cart-quantity-adjust], .ae-cart-drawer__quantity-input").forEach((control) => {
      control.disabled = isBusy;
    });

    lineItem.querySelectorAll(".ae-cart-drawer__remove-link").forEach((link) => {
      if (isBusy) {
        link.setAttribute("aria-disabled", "true");
        link.setAttribute("tabindex", "-1");
      } else {
        link.removeAttribute("aria-disabled");
        link.removeAttribute("tabindex");
      }
    });

    lineItem.querySelectorAll("line-item-quantity").forEach(syncQuantityButtons);
  }

  function setAccessoryFormBusy(form, isBusy) {
    if (!form) {
      return;
    }

    form.dataset.aeSubmitting = isBusy ? "true" : "false";

    form.querySelectorAll('[type="submit"]').forEach((submitButton) => {
      if (isBusy) {
        submitButton.setAttribute("aria-busy", "true");
        submitButton.disabled = true;
      } else {
        submitButton.removeAttribute("aria-busy");
        submitButton.disabled = false;
      }
    });
  }

  function showToast(root, message) {
    const toast = root.querySelector("[data-ae-cart-toast]");

    if (!toast || !message) {
      return;
    }

    window.clearTimeout(toastTimers.get(root));
    toast.textContent = message;
    toast.setAttribute("aria-hidden", "false");
    toast.dataset.state = "open";

    toastTimers.set(
      root,
      window.setTimeout(() => {
        toast.dataset.state = "closed";
        toast.setAttribute("aria-hidden", "true");
      }, toastDelay)
    );
  }

  function markButtonAdded(root, form) {
    const submitButton = form?.querySelector('[type="submit"]');

    if (!submitButton) {
      return;
    }

    if (!submitButton.dataset.aeOriginalText) {
      submitButton.dataset.aeOriginalText = submitButton.textContent.trim();
    }

    submitButton.textContent = root.dataset.aeCartAddedMessage || submitButton.dataset.aeOriginalText;
    submitButton.classList.add("ae-cart-drawer__button-added");

    window.setTimeout(() => {
      if (!submitButton.isConnected) {
        return;
      }

      submitButton.textContent = submitButton.dataset.aeOriginalText;
      submitButton.classList.remove("ae-cart-drawer__button-added");
    }, toastDelay);
  }

  async function addAccessoryToCart(root, form) {
    if (!form || form.dataset.aeSubmitting === "true") {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setAccessoryFormBusy(form, true);

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

    const formData = new FormData(form);

    if (sectionsToBundle.length > 0) {
      formData.set("sections", [...new Set(sectionsToBundle)].join(","));
      formData.set("sections_url", window.location.pathname);
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      const responseJson = await response.json();

      if (!response.ok) {
        showToast(root, responseJson.description);
        return;
      }

      const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();

      if (responseJson.sections && Object.keys(responseJson.sections).length > 0) {
        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart: cartContent
            }
          })
        );
      } else {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { cart: cartContent } }));
      }

      window.setTimeout(() => {
        if (typeof root.show === "function") {
          root.show();
        }
      }, 0);
    } catch (error) {
      showToast(root, error.message);
    } finally {
      setAccessoryFormBusy(form, false);
    }
  }

  function onAccessorySubmit(event) {
    const form = event.target.closest?.("[data-ae-cart-accessory-form] form");

    if (!form || !event.currentTarget.contains(form)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    addAccessoryToCart(event.currentTarget, form);
  }

  function getAccessoryCarousel(target) {
    return target.closest?.(".ae-cart-drawer__upsell-carousel");
  }

  function getAccessoryCards(carousel) {
    return [...carousel.querySelectorAll(".ae-cart-drawer__upsell-list > .horizontal-product--cart-drawer")];
  }

  function getAccessoryStep(carousel) {
    const list = carousel.querySelector(".ae-cart-drawer__upsell-list");
    const card = list?.querySelector(".horizontal-product--cart-drawer");

    if (!list || !card) {
      return 0;
    }

    const gap = toPixelNumber(window.getComputedStyle(list).columnGap, 0);

    return card.getBoundingClientRect().width + gap;
  }

  function getAccessoryPageSize(carousel) {
    const list = carousel.querySelector(".ae-cart-drawer__upsell-list");
    const cards = getAccessoryCards(carousel);
    const step = getAccessoryStep(carousel);

    if (!list || cards.length === 0 || step <= 0) {
      return 1;
    }

    const gap = toPixelNumber(window.getComputedStyle(list).columnGap, 0);
    const pageSize = Math.floor((carousel.clientWidth + gap + 1) / step);

    return Math.min(cards.length, Math.max(1, pageSize));
  }

  function getAccessoryPageCount(carousel) {
    const cards = getAccessoryCards(carousel);
    const pageSize = getAccessoryPageSize(carousel);

    return Math.max(0, Math.ceil(cards.length / pageSize));
  }

  function getAccessoryPage(carousel) {
    const cards = getAccessoryCards(carousel);
    const step = getAccessoryStep(carousel);
    const pageSize = getAccessoryPageSize(carousel);
    const pageCount = getAccessoryPageCount(carousel);

    if (cards.length === 0 || step <= 0) {
      return 0;
    }

    return Math.min(pageCount - 1, Math.max(0, Math.round(carousel.scrollLeft / (step * pageSize))));
  }

  function getAccessoryDotsContainer(carousel) {
    return carousel.closest(".ae-cart-drawer__upsells")?.querySelector("[data-ae-cart-accessory-dots]");
  }

  function animateAccessoryScroll(carousel, targetScrollLeft) {
    const previousFrame = accessoryScrollAnimations.get(carousel);

    if (previousFrame) {
      window.cancelAnimationFrame(previousFrame);
    }

    const startScrollLeft = carousel.scrollLeft;
    const distance = targetScrollLeft - startScrollLeft;

    if (Math.abs(distance) < 1) {
      carousel.scrollLeft = targetScrollLeft;
      queueAccessoryDotsSync(carousel);
      return;
    }

    const duration = 320;
    const startTime = Date.now();
    carousel.classList.add("is-programmatic-scrolling");

    const step = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);

      carousel.scrollLeft = startScrollLeft + distance * eased;
      queueAccessoryDotsSync(carousel);

      if (progress < 1) {
        accessoryScrollAnimations.set(carousel, window.requestAnimationFrame(step));
        return;
      }

      carousel.scrollLeft = targetScrollLeft;
      carousel.classList.remove("is-programmatic-scrolling");
      accessoryScrollAnimations.delete(carousel);
      queueAccessoryDotsSync(carousel);
    };

    accessoryScrollAnimations.set(carousel, window.requestAnimationFrame(step));
  }

  function scrollAccessoryCarouselToPage(carousel, page) {
    const cards = getAccessoryCards(carousel);
    const step = getAccessoryStep(carousel);
    const pageSize = getAccessoryPageSize(carousel);
    const pageCount = getAccessoryPageCount(carousel);

    if (cards.length === 0 || step <= 0) {
      return;
    }

    const targetPage = Math.min(pageCount - 1, Math.max(0, page));
    const targetCardIndex = Math.min(cards.length - 1, targetPage * pageSize);
    const maxScrollLeft = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    const targetScrollLeft = Math.min(maxScrollLeft, Math.max(0, cards[targetCardIndex]?.offsetLeft ?? targetCardIndex * step));

    animateAccessoryScroll(carousel, targetScrollLeft);
    syncAccessoryDots(carousel, targetPage);
  }

  function syncAccessoryDots(carousel, currentPage = getAccessoryPage(carousel)) {
    const container = getAccessoryDotsContainer(carousel);
    const pageCount = getAccessoryPageCount(carousel);

    if (!container) {
      return;
    }

    container.hidden = pageCount <= 1;
    container.setAttribute("aria-hidden", pageCount <= 1 ? "true" : "false");

    if (container.dataset.aeDotCount !== String(pageCount)) {
      container.replaceChildren(
        ...Array.from({ length: pageCount }, (_, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "ae-cart-drawer__upsell-dot";
          button.dataset.aeCartAccessoryDot = String(index);
          button.setAttribute("aria-label", `Show accessories page ${index + 1} of ${pageCount}`);
          return button;
        })
      );

      container.dataset.aeDotCount = String(pageCount);
    }

    container.querySelectorAll("[data-ae-cart-accessory-dot]").forEach((button, index) => {
      button.setAttribute("aria-current", index === currentPage ? "true" : "false");
    });
  }

  function queueAccessoryDotsSync(carousel) {
    if (accessoryDotFrames.has(carousel)) {
      return;
    }

    accessoryDotFrames.set(
      carousel,
      window.requestAnimationFrame(() => {
        accessoryDotFrames.delete(carousel);
        syncAccessoryDots(carousel);
      })
    );
  }

  function setupAccessoryCarousels(root) {
    root.querySelectorAll(".ae-cart-drawer__upsell-carousel").forEach((carousel) => {
      syncAccessoryDots(carousel);
    });
  }

  function queueAccessoryRootSync(root) {
    if (accessoryRootFrames.has(root)) {
      return;
    }

    accessoryRootFrames.set(
      root,
      window.requestAnimationFrame(() => {
        accessoryRootFrames.delete(root);
        syncDrawerLayoutFromInner(root);
        root.querySelectorAll("line-item-quantity").forEach(syncQuantityButtons);
        setupAccessoryCarousels(root);
      })
    );
  }

  function snapAccessoryCarousel(carousel) {
    scrollAccessoryCarouselToPage(carousel, getAccessoryPage(carousel));
  }

  function onAccessoryDragStart(event) {
    if (activeAccessoryDrag || event.isPrimary === false || event.button !== 0) {
      return;
    }

    if (event.type === "pointerdown" && event.pointerType !== "mouse") {
      return;
    }

    const carousel = getAccessoryCarousel(event.target);

    if (!carousel || !event.currentTarget.contains(carousel) || carousel.scrollWidth <= carousel.clientWidth) {
      return;
    }

    activeAccessoryDrag = {
      carousel,
      source: event.type === "pointerdown" ? "pointer" : "mouse",
      pointerId: event.pointerId ?? null,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: carousel.scrollLeft,
      dragged: false
    };

    carousel.classList.add("is-pointer-active");

    if (activeAccessoryDrag.source === "pointer") {
      carousel.setPointerCapture?.(event.pointerId);
    }
  }

  function onAccessoryDragMove(event) {
    const state = activeAccessoryDrag;
    const carousel = state?.carousel;

    if (!carousel || !state) {
      return;
    }

    if (state.source === "pointer" && (event.type !== "pointermove" || state.pointerId !== event.pointerId)) {
      return;
    }

    if (state.source === "mouse" && event.type !== "mousemove") {
      return;
    }

    const distanceX = event.clientX - state.startX;
    const distanceY = event.clientY - state.startY;

    if (!state.dragged && Math.abs(distanceY) >= accessoryDragThreshold && Math.abs(distanceY) > Math.abs(distanceX)) {
      finishAccessoryDrag(event, false);
      return;
    }

    if (!state.dragged && Math.abs(distanceX) >= accessoryDragThreshold && Math.abs(distanceX) > Math.abs(distanceY)) {
      state.dragged = true;
      carousel.classList.add("is-pointer-dragging");
    }

    if (!state.dragged) {
      return;
    }

    event.preventDefault();
    carousel.scrollLeft = state.startScrollLeft - distanceX;
  }

  function finishAccessoryDrag(event, shouldSnap = true) {
    const state = activeAccessoryDrag;
    const carousel = state?.carousel;

    if (!carousel || !state) {
      return;
    }

    if (state.source === "pointer" && (!event.type.startsWith("pointer") || state.pointerId !== event.pointerId)) {
      return;
    }

    if (state.source === "mouse" && event.type !== "mouseup" && event.type !== "mouseleave") {
      return;
    }

    activeAccessoryDrag = null;
    carousel.classList.remove("is-pointer-active", "is-pointer-dragging");

    if (state.source === "pointer" && carousel.hasPointerCapture?.(event.pointerId)) {
      carousel.releasePointerCapture(event.pointerId);
    }

    if (!state.dragged) {
      return;
    }

    carousel.dataset.aeSuppressClick = "true";

    window.setTimeout(() => {
      delete carousel.dataset.aeSuppressClick;
    }, 0);

    if (shouldSnap) {
      snapAccessoryCarousel(carousel);
    }
  }

  function onAccessoryScroll(event) {
    const carousel = event.target;

    if (!carousel.matches?.(".ae-cart-drawer__upsell-carousel")) {
      return;
    }

    queueAccessoryDotsSync(carousel);
  }

  function onAccessoryNativeDragStart(event) {
    if (!getAccessoryCarousel(event.target)) {
      return;
    }

    event.preventDefault();
  }

  function onRootClick(event) {
    const accessoryDot = event.target.closest?.("[data-ae-cart-accessory-dot]");

    if (accessoryDot && event.currentTarget.contains(accessoryDot)) {
      const carousel = accessoryDot.closest(".ae-cart-drawer__upsells")?.querySelector(".ae-cart-drawer__upsell-carousel");

      if (carousel) {
        event.preventDefault();
        scrollAccessoryCarouselToPage(carousel, toNumber(accessoryDot.dataset.aeCartAccessoryDot, 0));
      }

      return;
    }

    const accessoryCarousel = getAccessoryCarousel(event.target);

    if (accessoryCarousel?.dataset.aeSuppressClick === "true") {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const disabledRemoveLink = event.target.closest('.ae-cart-drawer__remove-link[aria-disabled="true"]');

    if (disabledRemoveLink) {
      event.preventDefault();
      return;
    }

    const button = event.target.closest("[data-ae-cart-quantity-adjust]");

    if (!button || !event.currentTarget.contains(button) || button.disabled) {
      return;
    }

    const quantityContainer = button.closest("line-item-quantity");
    const input = quantityContainer?.querySelector("[data-line-key]");

    if (!input || input.disabled) {
      return;
    }

    const min = toNumber(input.getAttribute("min"), 0);
    const maxAttribute = input.getAttribute("max");
    const max = maxAttribute === null || maxAttribute === "" ? Infinity : toNumber(maxAttribute, Infinity);
    const step = toNumber(input.getAttribute("step"), 1);
    const currentValue = toNumber(input.value, min);
    const direction = button.getAttribute("data-ae-cart-quantity-adjust") === "increase" ? 1 : -1;
    const nextValue = Math.min(max, Math.max(min, currentValue + step * direction));

    if (nextValue === currentValue) {
      syncQuantityButtons(quantityContainer);
      return;
    }

    input.value = String(nextValue);
    syncQuantityButtons(quantityContainer);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function onRootChange(event) {
    if (!event.target.matches(".ae-cart-drawer__quantity-input")) {
      return;
    }

    syncQuantityButtons(event.target.closest("line-item-quantity"));
  }

  function onLineItemWillChange(event) {
    setLineBusy(event.target.closest("line-item"), true);
  }

  function onLineItemDone(event) {
    setLineBusy(event.target.closest("line-item"), false);
  }

  function onVariantAdded(event) {
    const root = event.currentTarget;
    const form = event.target.closest('form[is="product-form"]');

    markButtonAdded(root, form);
    showToast(root, root.dataset.aeCartAddedMessage);
  }

  function onCartError(event) {
    const errorMessage = event.detail?.error;
    showToast(event.currentTarget, errorMessage);
  }

  function isDeferredCartOpenEvent(event) {
    return Boolean(event.target.closest?.(deferredOpenFormSelector));
  }

  function getCartDrawerRoot() {
    return document.querySelector(rootSelector);
  }

  function getUpdatedDrawerFromCartChange(event) {
    const sections = event.detail?.cart?.sections;

    if (!sections || typeof sections !== "object") {
      return null;
    }

    const drawerHtml = Object.values(sections).find(
      (sectionHtml) => typeof sectionHtml === "string" && sectionHtml.includes("data-ae-cart-drawer")
    );

    if (!drawerHtml) {
      return null;
    }

    return new DOMParser().parseFromString(drawerHtml, "text/html").querySelector(rootSelector);
  }

  function syncDrawerLayoutFromInner(root) {
    const inner = root?.querySelector(".ae-cart-drawer__inner");

    if (!root || !inner) {
      return;
    }

    root.classList.toggle(noUpsellsDrawerClass, inner.classList.contains(noUpsellsInnerClass));
  }

  function syncDrawerLayoutFromServer(root, updatedDrawer) {
    const currentInner = root?.querySelector(".ae-cart-drawer__inner");
    const updatedInner = updatedDrawer?.querySelector(".ae-cart-drawer__inner");

    if (!root || !updatedDrawer) {
      return;
    }

    const hasNoUpsells = updatedDrawer.classList.contains(noUpsellsDrawerClass);

    root.classList.toggle(noUpsellsDrawerClass, hasNoUpsells);

    if (currentInner && updatedInner) {
      currentInner.classList.toggle(noUpsellsInnerClass, updatedInner.classList.contains(noUpsellsInnerClass));
    }
  }

  function onCartLayoutChange(event) {
    const root = getCartDrawerRoot();
    const updatedDrawer = getUpdatedDrawerFromCartChange(event);

    if (!root || !updatedDrawer) {
      return;
    }

    // Impact replaces only the inner markup, leaving its layout classes stale.
    // Match the incoming section before that replacement to prevent grid overlap.
    syncDrawerLayoutFromServer(root, updatedDrawer);

    const refreshDelay = event.detail?.baseEvent === "variant:add" ? 0 : 1260;

    window.setTimeout(() => {
      syncDrawerLayoutFromInner(root);
    }, refreshDelay);
  }

  function clearDeferredCartOpen() {
    deferredCartOpenPending = false;
    window.clearTimeout(deferredCartOpenTimer);
    deferredCartOpenTimer = null;
  }

  function onDeferredCartVariantAdd(event) {
    if (!isDeferredCartOpenEvent(event)) {
      return;
    }

    if (event.detail && typeof event.detail === "object") {
      event.detail.blockCartDrawerOpening = true;
    }

    deferredCartOpenPending = true;
    window.clearTimeout(deferredCartOpenTimer);
    deferredCartOpenTimer = window.setTimeout(clearDeferredCartOpen, 4000);
  }

  function onDeferredCartChange(event) {
    if (!deferredCartOpenPending || event.detail?.baseEvent !== "variant:add") {
      return;
    }

    clearDeferredCartOpen();

    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        const root = getCartDrawerRoot();

        if (window.themeVariables?.settings?.cartType !== "drawer" || typeof root?.show !== "function") {
          return;
        }

        root.show();
      });
    }, 80);
  }

  function initCartDrawer(root) {
    if (root.hasAttribute(initializedAttribute)) {
      return;
    }

    root.setAttribute(initializedAttribute, "true");
    root.addEventListener("submit", onAccessorySubmit, true);
    root.addEventListener("pointerdown", onAccessoryDragStart);
    root.addEventListener("pointermove", onAccessoryDragMove);
    root.addEventListener("pointerup", (event) => finishAccessoryDrag(event));
    root.addEventListener("pointercancel", (event) => finishAccessoryDrag(event, false));
    root.addEventListener("mousedown", onAccessoryDragStart);
    root.addEventListener("mousemove", onAccessoryDragMove);
    root.addEventListener("mouseup", (event) => finishAccessoryDrag(event));
    root.addEventListener("mouseleave", (event) => finishAccessoryDrag(event));
    root.addEventListener("scroll", onAccessoryScroll, true);
    root.addEventListener("dragstart", onAccessoryNativeDragStart);
    root.addEventListener("click", onRootClick);
    root.addEventListener("change", onRootChange);
    root.addEventListener("line-item:will-change", onLineItemWillChange);
    root.addEventListener("line-item:error", onLineItemDone);
    root.addEventListener("line-item:change", onLineItemDone);
    root.addEventListener("variant:add", onVariantAdded);
    root.addEventListener("cart:error", onCartError);
    root.querySelectorAll("line-item-quantity").forEach(syncQuantityButtons);
    setupAccessoryCarousels(root);

    if (!accessoryMutationObservers.has(root)) {
      const observer = new MutationObserver(() => queueAccessoryRootSync(root));
      observer.observe(root, { childList: true, subtree: true });
      accessoryMutationObservers.set(root, observer);
    }
  }

  function initDocumentListeners() {
    if (document.documentElement.hasAttribute(documentInitializedAttribute)) {
      return;
    }

    document.documentElement.setAttribute(documentInitializedAttribute, "true");
    document.addEventListener("cart:change", onCartLayoutChange, true);
    document.addEventListener("variant:add", onDeferredCartVariantAdd, true);
    document.addEventListener("cart:change", onDeferredCartChange);
  }

  function initAll() {
    initDocumentListeners();
    document.querySelectorAll(rootSelector).forEach(initCartDrawer);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
})();
